import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { uploadImage } from "../../../Components/UploadFile/upload";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slateLt: "#9ca8b7", line: "#eeebe6", peach: "#ffebd3",
  redTx: "#9b2c2c",
};

const PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='140'%3E%3Crect width='200' height='140' fill='%23f0ebe6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca8b7' font-size='13'%3ENo Image%3C/text%3E%3C/svg%3E";

interface Props {
  imageUrl: string;
  onImageChange: (url: string) => void;
  compact?: boolean;
}

const ImageUploader: React.FC<Props> = ({ imageUrl, onImageChange, compact }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const API_URL = process.env.REACT_APP_API_URL || "";

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Image must be under 2MB");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const path = await uploadImage(file);
      if (path) {
        const fullUrl = `${API_URL}/../${path}`;
        onImageChange(fullUrl);
      }
    } catch {
      setError("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 56, height: 56, borderRadius: "10px", overflow: "hidden",
            border: `1.5px solid ${T.line}`, flexShrink: 0, cursor: "pointer",
            position: "relative",
            "&:hover .overlay": { opacity: 1 },
          }}
          onClick={() => inputRef.current?.click()}
        >
          <Box
            component="img"
            src={imageUrl || PH}
            onError={(e: any) => { e.target.src = PH; }}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Box className="overlay" sx={{
            position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: 0, transition: "opacity 0.2s",
          }}>
            {uploading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <AddPhotoAlternateOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />}
          </Box>
        </Box>
        {imageUrl && (
          <IconButton size="small" onClick={() => onImageChange("")} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleInputChange} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          borderRadius: "12px",
          border: `2px dashed ${dragOver ? T.gold : T.line}`,
          bgcolor: dragOver ? `${T.peach}50` : T.bg,
          transition: "all 0.2s",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {imageUrl ? (
          <Box sx={{ position: "relative" }}>
            <Box
              component="img"
              src={imageUrl}
              onError={(e: any) => { e.target.src = PH; }}
              sx={{
                width: "100%",
                height: 160,
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box sx={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)",
            }} />
            <Box sx={{ position: "absolute", bottom: 10, right: 10, display: "flex", gap: 0.5 }}>
              <Button
                size="small"
                onClick={() => inputRef.current?.click()}
                startIcon={uploading ? <CircularProgress size={12} color="inherit" /> : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: "none", fontSize: 11, fontWeight: 600,
                  bgcolor: "rgba(255,255,255,0.9)", color: T.navy,
                  borderRadius: "8px", px: 1.5, py: 0.3,
                  backdropFilter: "blur(4px)",
                  "&:hover": { bgcolor: T.white },
                }}
              >
                Replace
              </Button>
              <IconButton
                size="small"
                onClick={() => onImageChange("")}
                sx={{
                  bgcolor: "rgba(255,255,255,0.9)", width: 28, height: 28,
                  "&:hover": { bgcolor: "#fee2e2" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14, color: T.redTx }} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => inputRef.current?.click()}
            sx={{
              py: 4, px: 2, textAlign: "center", cursor: "pointer",
              "&:hover": { bgcolor: `${T.peach}30` },
              transition: "background 0.15s",
            }}
          >
            {uploading ? (
              <CircularProgress size={28} sx={{ color: T.gold, mb: 1 }} />
            ) : (
              <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: T.gold, opacity: 0.6, mb: 1 }} />
            )}
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mb: 0.3 }}>
              {uploading ? "Uploading..." : "Drop image here or click to upload"}
            </Typography>
            <Typography sx={{ fontSize: 10, color: T.slateLt }}>
              JPEG, PNG, WebP up to 2MB
            </Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Typography sx={{ fontSize: 11, color: T.redTx, mt: 0.5 }}>{error}</Typography>
      )}

      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleInputChange} />
    </Box>
  );
};

export default ImageUploader;
